class HzProductForm extends HTMLElement {
  constructor() {
    super();
    this.cartDrawer = document.querySelector('cart-drawer');
    this.allProducts = this.querySelectorAll('.arFeatProduct'); // Scope to this custom element's children

    this.initEventListeners();
    this.initPubSubSubscriptions();
  }

  initEventListeners() {
    // Event delegation for custom UI interactions within this custom element
    this.addEventListener('click', this.handleCustomUIClick.bind(this));

    // Global click listener for outside clicks to reset UI
    document.addEventListener('click', this.handleOutsideClick.bind(this));

    // Event delegation for dynamically created forms within this custom element
    this.addEventListener('submit', this.handleDynamicFormSubmit.bind(this));
  }

  initPubSubSubscriptions() {
    // Subscribe to cart updates to ensure UI consistency across the theme
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.onCartError.bind(this));
  }

  disconnectedCallback() {
    // Clean up event listeners and subscriptions to prevent memory leaks
    this.removeEventListener('click', this.handleCustomUIClick.bind(this));
    document.removeEventListener('click', this.handleOutsideClick.bind(this));
    this.removeEventListener('submit', this.handleDynamicFormSubmit.bind(this));

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.cartErrorUnsubscriber) {
      this.cartErrorUnsubscriber();
    }
  }

  // --- Custom UI Logic ---

  handleCustomUIClick(event) {
    const cartBtn = event.target.closest('.hz__cdpItem__cartBtn');
    if (cartBtn) {
      this.handleCartButtonClick(cartBtn);
      return;
    }

    const sizeBtn = event.target.closest('.hz__cdpiVariant__sizeBtn');
    if (sizeBtn) {
      this.handleSizeButtonClick(sizeBtn);
      return;
    }
  }

  handleCartButtonClick(cartBtn) {
    const product = cartBtn.closest('.arFeatProduct');
    if (!product) return;

    // Hide variant selectors and show cart button for all other products
    this.allProducts.forEach((p) => {
      const sizeContainer = p.querySelector('.hz__cdpiVariants__sizes');
      const colorContainer = p.querySelector('.hz__cdpiVariants__colors');
      const btn = p.querySelector('.hz__cdpItem__cartBtn');
      if (sizeContainer && colorContainer && btn) {
        if (p !== product) {
          sizeContainer.style.display = 'none';
          colorContainer.style.display = 'none';
          btn.style.display = 'block';
        }
      }
    });

    // Show size selector for the clicked product
    const sizeContainer = product.querySelector('.hz__cdpiVariants__sizes');
    if (sizeContainer) sizeContainer.style.display = 'flex';
    cartBtn.style.display = 'none';
  }

  handleSizeButtonClick(sizeBtn) {
    const product = sizeBtn.closest('.arFeatProduct');
    if (!product) return;

    // Hide size selector and show color selector for the clicked product
    const colorContainer = product.querySelector('.hz__cdpiVariants__colors');
    const sizeContainer = product.querySelector('.hz__cdpiVariants__sizes');
    if (colorContainer) colorContainer.style.display = 'flex';
    if (sizeContainer) sizeContainer.style.display = 'none';

    // Hide color selectors for all other products
    this.allProducts.forEach((p) => {
      if (p === product) return;
      const otherColor = p.querySelector('.hz__cdpiVariants__colors');
      if (otherColor) otherColor.style.display = 'none';
    });

    this.populateColorVariants(sizeBtn);
  }

  handleOutsideClick(event) {
    // Check if the click was inside the cart drawer, if so, do not reset UI
    if (this.cartDrawer && this.cartDrawer.contains(event.target)) {
      return;
    }

    this.allProducts.forEach((product) => {
      const sizeContainer = product.querySelector('.hz__cdpiVariants__sizes');
      const colorContainer = product.querySelector('.hz__cdpiVariants__colors');
      const cartBtn = product.querySelector('.hz__cdpItem__cartBtn');

      if (sizeContainer && colorContainer && cartBtn) {
        // If the click was not within any of the product's custom UI elements, reset it
        if (
          !sizeContainer.contains(event.target) &&
          !colorContainer.contains(event.target) &&
          !cartBtn.contains(event.target)
        ) {
          sizeContainer.style.display = 'none';
          colorContainer.style.display = 'none';
          cartBtn.style.display = 'block';
        }
      }
    });
  }

  populateColorVariants(sizeBtn) {
    const variants = JSON.parse(sizeBtn.dataset.variants);
    const colorContainer = sizeBtn.closest('.hz__cdpItem__variants')?.querySelector('.hz__cdpiVariants__colors');
    if (!colorContainer) return;

    colorContainer.innerHTML = ''; // Clear previous color options
    colorContainer.style.display = 'flex';
    const sizeContainer = sizeBtn.closest('.hz__cdpItem__variants')?.querySelector('.hz__cdpiVariants__sizes');
    if (sizeContainer) sizeContainer.style.display = 'none';

    variants.forEach((variant) => {
      const form = document.createElement('form');
      form.method = 'post';
      form.action = routes.cart_add_url; // Use the global routes object
      form.classList.add('hz__cdpiVariant__form');

      const submitBtn = document.createElement('input');
      submitBtn.type = 'submit';
      submitBtn.value = variant.option2; // Assuming option2 is the color
      submitBtn.classList.add('hz__cdpiVariant__btn', 'hz__btn', variant.option2.toLowerCase().replace(/\s/g, '-')); // Add variant.option2 as class for color styling, sanitized

      if (!variant.available) {
        submitBtn.disabled = true;
        submitBtn.classList.add('hz__cdpiVariant__soldout');
        submitBtn.value = 'Sold Out'; // Indicate sold out state
      }

      form.innerHTML = `
        <label class="hz__cdpiVariant__item">
          <input type="hidden" name="id" value="${variant.id}">
          <input type="hidden" name="quantity" value="1">
        </label>
      `;
      form.querySelector('.hz__cdpiVariant__item').appendChild(submitBtn);

      colorContainer.appendChild(form);
    });
  }

  // --- Add to Cart Logic (Integrated from Dawn's product-form.js and cart.js) ---

  async handleDynamicFormSubmit(event) {
    const form = event.target.closest('.hz__cdpiVariant__form');
    if (!form) return;

    event.preventDefault(); // Prevent default form submission

    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity'));

    const submitBtn = form.querySelector('.hz__cdpiVariant__btn');
    const originalText = submitBtn.value;
    submitBtn.disabled = true;
    submitBtn.value = 'Adding...'; // Show loading state

    try {
      // Determine sections to render for partial UI updates
      const cartComponent = this.cartDrawer || document.querySelector('cart-notification');
      let sectionsToRender = [];
      if (cartComponent && typeof cartComponent.getSectionsToRender === 'function') {
        sectionsToRender = cartComponent.getSectionsToRender().map(section => section.section);
      } else {
        // Fallback sections if cart component not found or doesn't have getSectionsToRender
        // These are common sections that usually need updating
        sectionsToRender = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];
      }

      const body = JSON.stringify({
        id: variantId,
        quantity: quantity,
        sections: sectionsToRender,
        sections_url: window.location.pathname, // Current page URL for section context
      });

      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body,
      });

      const parsedState = await response.json();

      if (!response.ok) {
        // Handle API errors (e.g., inventory issues, validation errors)
        const errorMessage = parsedState.description || parsedState.message || 'Unknown error adding to cart.';
        this.onCartError({ errors: errorMessage });
        return; // Stop further processing on error
      }

      // Update UI based on sections received from Shopify
      this.updateSections(parsedState.sections);

      // Publish cart update event for other components to react (e.g., product-info.js, quantity-input.js)
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'hz-product-form', // Indicate source of the update
        productVariantId: variantId,
        cartData: parsedState,
      });

      // Reset custom UI after successful add
      this.resetCustomUI(form);

      // Open cart drawer if it exists and has an open method
      if (this.cartDrawer && typeof this.cartDrawer.open === 'function') {
        this.cartDrawer.open();
        // Call initCartDrawerSwiper if it's a global function (from user's original script)
        if (typeof window.initCartDrawerSwiper === 'function') {
          window.initCartDrawerSwiper();
        }
      }

    } catch (error) {
      console.error('Network or unexpected error adding to cart:', error);
      this.onCartError({ errors: 'There was an error adding to cart. Please try again.' });
    } finally {
      // Always reset button state
      submitBtn.disabled = false;
      submitBtn.value = originalText;
    }
  }

  updateSections(sections) {
    for (const sectionId in sections) {
      if (sections.hasOwnProperty(sectionId)) {
        const elementToUpdate = document.getElementById(sectionId);
        if (elementToUpdate) {
          // Create a temporary div to parse the incoming HTML safely
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = sections[sectionId];

          // Find the specific element within the new section HTML that matches the ID
          // or use the entire new section HTML if no specific selector is provided
          const newContentElement = tempDiv.querySelector(`#${sectionId}`) || tempDiv;

          if (newContentElement) {
            // Special handling for cart-drawer to toggle 'is-empty' class
            if (sectionId === 'CartDrawer') {
              const cartDrawerElement = document.querySelector('cart-drawer');
              if (cartDrawerElement) {
                // Check if the new content indicates an empty cart
                const isEmpty = newContentElement.querySelector('.drawer__inner.is-empty') !== null;
                cartDrawerElement.classList.toggle('is-empty', isEmpty);
              }
            }
            // Update the innerHTML of the existing element
            elementToUpdate.innerHTML = newContentElement.innerHTML;
          }
        }
      }
    }
  }

  resetCustomUI(form) {
    const product = form.closest('.arFeatProduct');
    if (product) {
      const colorContainer = product.querySelector('.hz__cdpiVariants__colors');
      const sizeContainer = product.querySelector('.hz__cdpiVariants__sizes');
      const cartBtn = product.querySelector('.hz__cdpItem__cartBtn');
      if (colorContainer) colorContainer.style.display = 'none';
      if (sizeContainer) sizeContainer.style.display = 'none';
      if (cartBtn) cartBtn.style.display = 'block';
    }
  }

  // --- Pub/Sub Event Handlers ---

  onCartUpdate(event) {
    // This method ensures global UI elements like the cart bubble are updated
    // regardless of where the cart update originated.
    console.log('Cart updated (via Pub/Sub):', event.cartData);

    // Update cart bubble count
    const cartBubble = document.getElementById('cart-icon-bubble');
    if (cartBubble && event.cartData && event.cartData.item_count !== undefined) {
      const countBubble = cartBubble.querySelector('.cart-count-bubble');
      if (countBubble) {
        countBubble.querySelector('span[aria-hidden="true"]').textContent = event.cartData.item_count;
        countBubble.querySelector('.visually-hidden').textContent = `Cart count: ${event.cartData.item_count}`;
      }
      cartBubble.classList.toggle('empty', event.cartData.item_count === 0);
    }

    // Update live region text for accessibility
    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);
      cartStatus.textContent = `Cart updated. Total items: ${event.cartData.item_count}.`;
      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }

  onCartError(event) {
    console.error('Cart error (via Pub/Sub):', event.errors);
    const errorsContainer = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errorsContainer) {
      errorsContainer.textContent = event.errors;
      errorsContainer.classList.remove('hidden'); // Ensure the error message is visible
    }
  }
}

customElements.define('hz-product-form', HzProductForm);
